import { useCallback, useMemo } from 'react';
import { registerMutation, useDataFetchingClient, useMutation } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useReadFromTableCache, useWriteToTableCache } from 'framework-listing-lib/internal/hooks/useFetchCrmObjectTypeCache';
import { UNFILED_ASSETS_ID } from 'framework-listing-lib/internal/Folders/constants/folders';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { GET_FOLDERS_FIELD_NAME } from './useFetchFolders';
import { handleUpdateFolderCountsAfterMove } from 'framework-listing-lib/internal/Folders/utils/cache';
import useUnfiledAssetVariables from 'framework-listing-lib/internal/Folders/hooks/useUnfiledAssetVariables';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import useCrmSearchQuery from 'framework-listing-lib/hooks/useCrmSearchQuery';
import useUpdateRecentFolders from 'framework-listing-lib/internal/Folders/hooks/useUpdateRecentFolders';
import useMaybeApolloClient from './useMaybeApolloClient';
const MOVE_ASSET_TO_FOLDER_FIELD_NAME = 'moveAssetToFolder';
const MOVE_ASSET_TO_FOLDER = registerMutation({
  fieldName: MOVE_ASSET_TO_FOLDER_FIELD_NAME,
  args: ['assetIds', 'filterGroups', 'newFolderId', 'objectType', 'openFoldersMap', 'previousFolderId', 'selectedObjectsCount', 'selectionMode'],
  fetcher({
    assetIds,
    filterGroups,
    newFolderId,
    objectType,
    openFoldersMap,
    previousFolderId,
    selectedObjectsCount,
    selectionMode
  }) {
    const data = {
      objectTypeId: objectType,
      maybeDestinationFolderId: newFolderId === -1 ? undefined : newFolderId
    };
    if (selectionMode && selectionMode === 'all') {
      data.filterGroups = filterGroups;
    } else {
      data.objectIds = assetIds;
    }
    return http.put('framework/folders/v1/move-objects', {
      data
    }).then(() => ({
      assetIds,
      filterGroups,
      newFolderId,
      objectType,
      openFoldersMap,
      previousFolderId,
      selectedObjectsCount,
      selectionMode
    }));
  }
});
export default function useFolderActions() {
  const client = useDataFetchingClient();
  const maybeApolloClient = useMaybeApolloClient();
  const objectType = useObjectTypeId();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const writeToTableCache = useWriteToTableCache();
  const readFromTableCache = useReadFromTableCache();
  const unfiledAssetVariables = useUnfiledAssetVariables();
  const isFoldersMode = useIsFoldersMode();
  const {
    filterGroups
  } = useCrmSearchQuery();
  const updateRecentFolders = useUpdateRecentFolders();
  const [moveAssetToFolderMutation, {
    error,
    loading: isMovingAssetToFolder
  }] = useMutation(MOVE_ASSET_TO_FOLDER, {
    update(cache, {
      data
    }) {
      const {
        [MOVE_ASSET_TO_FOLDER_FIELD_NAME]: response
      } = data || {
        [MOVE_ASSET_TO_FOLDER_FIELD_NAME]: undefined
      };
      if (!response) {
        return;
      }
      /**
       * Keep a reference of current table cache before moving asset
       */
      const currentTableCache = readFromTableCache();
      const {
        totalAssetsInPreviousFolder,
        totalAssetsInNewFolder
      } = handleUpdateFolderCountsAfterMove({
        dfcClient: client,
        maybeApolloClient,
        moveAssetResponse: response,
        unfiledAssetVariables
      });

      /**
       * If cache is found, remove "moved asset" from list when in Folder Mode
       */
      if (currentTableCache && isFoldersMode) {
        if (response.selectionMode === 'all') {
          writeToTableCache(Object.assign({}, currentTableCache, {
            results: [],
            total: 0
          }));
        } else {
          writeToTableCache(Object.assign({}, currentTableCache, {
            results: currentTableCache.results.filter(crmObjectType => !response.assetIds.includes(parseInt(`${crmObjectType.objectId}`, 10))),
            total: Math.max(currentTableCache.total - response.assetIds.length, 0)
          }));
        }
      }

      /**
       * Look for new folder in cache and
       * trigger UI notifications via FloatingAlertStore
       */
      let newFolder;
      if (response.newFolderId !== UNFILED_ASSETS_ID) {
        cache.modify({
          fields: {
            [GET_FOLDERS_FIELD_NAME]: foldersCacheRef => {
              if (newFolder) {
                return foldersCacheRef;
              }
              newFolder = foldersCacheRef.find(crmFolder => crmFolder.objectId === response.newFolderId);
              return foldersCacheRef;
            }
          }
        });
      }

      /**
       * Wipe folders cache when not in folders mode
       * to ensure we'll render the correct folder counts when the user
       * selects folder mode.
       */
      if (!isFoldersMode) {
        cache.evict({
          fieldName: GET_FOLDERS_FIELD_NAME
        });
        cache.gc();
      }
      listingLibObserver.assetAddedToFolder(response, {
        newFolder,
        totalAssetsInNewFolder,
        totalAssetsInPreviousFolder
      });
    }
  });
  const moveAssetToFolder = useCallback(({
    assetIds,
    isRecentFolder,
    newFolderId,
    openFoldersMap,
    previousFolderId,
    selectedObjectsCount,
    selectionMode
  }) => {
    const args = {
      variables: {
        assetIds,
        filterGroups,
        newFolderId,
        objectType,
        openFoldersMap,
        previousFolderId,
        selectedObjectsCount,
        selectionMode
      }
    };
    return moveAssetToFolderMutation(args).then(response => {
      if (!response.data) {
        return undefined;
      }
      trackCrmTableInteraction({
        action: response.data.moveAssetToFolder.newFolderId === -1 ? 'asset-out-of-folder' : 'asset-to-folder',
        isRecentlyUsed: Boolean(isRecentFolder)
      });
      return response.data.moveAssetToFolder;
    }).then(response => {
      if (!response) {
        return undefined;
      }
      return updateRecentFolders(response.newFolderId, 'UPDATE').then(() => response).catch(() => response);
    });
  }, [filterGroups, moveAssetToFolderMutation, objectType, trackCrmTableInteraction, updateRecentFolders]);
  return useMemo(() => ({
    error: Boolean(error),
    moveAssetToFolder,
    loading: isMovingAssetToFolder
  }), [error, isMovingAssetToFolder, moveAssetToFolder]);
}